import ConversationRecord from "models/view-models/conversations/conversation-record";
import ConversationMessageRecord from "models/view-models/conversations/conversation-message-record";
import { MessageRole } from "models/enumerations/conversations/message-role";
import { ConversationMessageStatus } from "models/view-models/conversations/conversation-message-status";
import { ChatFinishReason } from "models/enumerations/conversations/chat-finish-reason";
import {
    ConversationMessageStore,
    createConversationMessageStore,
} from "utilities/atoms/conversations/current-conversation/create-conversation-message-store";
import { Reducer } from "utilities/atoms/atom-with-thunks-reducer";
import StringUtils from "utilities/string-utils";
import ConversationMessageFeedbackRecord from "models/view-models/conversations/conversation-message-feedback-record";
import { MemoryStatus } from "models/enumerations/conversations/memory-status";
import { ToolCallStatusAction } from "models/enumerations/conversations/tool-call-interaction-action";

export enum ConversationStateStatus {
    Uninitialized,
    ConversationLoading,
    ConversationReady,
    StreamingMessage,
    Error,
    ConversationMaxLengthReached,
    MessageFeedbackLoading,
}

export interface CurrentConversationState {
    streamingResponseAbortController?: AbortController;
    status: ConversationStateStatus;
    error?: Error;
    currentConversation?: ConversationRecord;
    conversationMessageStore?: ConversationMessageStore;
    lastAssistantMessageLengthLimited?: boolean;
}

export type ConversationsAction =
    | { type: "START_CONVERSATION"; message?: ConversationMessageRecord }
    | { type: "CONVERSATION_MAX_LENGTH_REACHED" }
    | {
          type: "CONVERSATION_READY";
          id?: string;
          title?: string;
      }
    | {
          type: "UPDATE_CONVERSATION_STATUS";
          status: ConversationStateStatus.StreamingMessage;
          streamingResponseAbortController: AbortController;
      }
    | {
          type: "UPDATE_CONVERSATION_STATUS";
          status: ConversationStateStatus;
          streamingResponseAbortController?: AbortController;
      }
    | {
          type: "UPDATE_MESSAGE";
          message: ConversationMessageRecord;
      }
    | { type: "LOADING_CONVERSATION"; id: string }
    | { type: "LOAD_CONVERSATION"; conversation: ConversationRecord }
    | { type: "CLEAR_CONVERSATION"; conversationId?: string }
    | { type: "CANCEL_PROMPT" }
    | {
          type: "LOADING_MESSAGE_FEEDBACK";
          status: ConversationStateStatus.MessageFeedbackLoading;
          messageId: string;
      }
    | {
          type: "UPDATE_MESSAGE_FEEDBACK";
          feedback: ConversationMessageFeedbackRecord;
      }
    | {
          type: "UPDATE_MEMORY_STATUS";
          messageId: string;
          memoryId: string;
          status: MemoryStatus;
      }
    | {
          type: "UPDATE_TOOL_CALL_STATUS";
          messageId: string;
          toolCallId: string;
          status: ToolCallStatusAction;
      }
    | { type: "RETRY"; ids: Array<string> }
    | { type: "EDIT"; ids: Array<string> }
    | { type: "ERROR"; error: Error }
    | { type: "RESET_ERROR" };

type ConversationReducer = Reducer<
    CurrentConversationState,
    ConversationsAction
>;

export const conversationReducer: ConversationReducer = (
    state: CurrentConversationState,
    action: ConversationsAction
): CurrentConversationState => {
    switch (action.type) {
        case "START_CONVERSATION":
            const conversationMessageStore =
                createConversationMessageStore().add(
                    action.message?.with({ index: 1 })
                );
            return {
                ...state,
                currentConversation: new ConversationRecord({
                    messages: conversationMessageStore.build(),
                }),
                status: ConversationStateStatus.ConversationLoading,
                conversationMessageStore,
                lastAssistantMessageLengthLimited: false,
            };

        case "CLEAR_CONVERSATION":
            if (
                action.conversationId &&
                action.conversationId !== state.currentConversation?.id
            ) {
                return state;
            }

            return {
                ...state,
                error: undefined,
                currentConversation: new ConversationRecord(),
                status: ConversationStateStatus.ConversationReady,
                conversationMessageStore: createConversationMessageStore(),
                lastAssistantMessageLengthLimited: false,
            };

        case "CONVERSATION_MAX_LENGTH_REACHED":
            return {
                ...state,
                status: ConversationStateStatus.ConversationMaxLengthReached,
            };

        case "CONVERSATION_READY":
            const currentConversationId =
                action.id ?? state.currentConversation?.id;

            if (
                !state.currentConversation ||
                !currentConversationId ||
                !state.conversationMessageStore
            ) {
                return {
                    ...state,
                    status: ConversationStateStatus.Error,
                    error: new Error(
                        "No current conversation to update with conversation ready."
                    ),
                };
            }

            return {
                ...state,
                status: ConversationStateStatus.ConversationReady,
                currentConversation: state.currentConversation.with({
                    id: currentConversationId,
                    messages: state.conversationMessageStore.build(
                        ConversationMessageStatus.Completed
                    ),
                }),
            };

        case "UPDATE_CONVERSATION_STATUS":
            // Clear the length-limited flag when starting a new message stream
            const shouldClearLengthFlag =
                action.status === ConversationStateStatus.StreamingMessage;

            return {
                ...state,
                status: action.status,
                streamingResponseAbortController:
                    action.streamingResponseAbortController,
                lastAssistantMessageLengthLimited: shouldClearLengthFlag
                    ? false
                    : state.lastAssistantMessageLengthLimited,
            };

        case "UPDATE_MESSAGE":
            return updateMessage(state, action.message);

        case "LOADING_CONVERSATION":
            return {
                ...state,
                currentConversation: new ConversationRecord({
                    id: action.id,
                }).addMessage(
                    new ConversationMessageRecord({
                        id: "loading-message",
                        content: "Loading conversation...",
                        role: MessageRole.Assistant,
                    })
                ),
                status: ConversationStateStatus.ConversationLoading,
                lastAssistantMessageLengthLimited: false,
            };

        case "LOAD_CONVERSATION":
            const loadConversationMessageStore =
                createConversationMessageStore().add(
                    ...action.conversation.messages
                );

            const loadedConversation = action.conversation.with({
                messages: loadConversationMessageStore.build(
                    ConversationMessageStatus.Completed
                ),
            });

            return {
                ...state,
                currentConversation: loadedConversation,
                conversationMessageStore: loadConversationMessageStore,
                status: ConversationStateStatus.ConversationReady,
                lastAssistantMessageLengthLimited:
                    isConversationLengthLimited(loadedConversation),
            };

        case "CANCEL_PROMPT":
            return handleCancelledPrompt(state);

        case "LOADING_MESSAGE_FEEDBACK":
            const message = state.currentConversation?.messages.find(
                (msg) => msg.id === action.messageId
            );
            if (!message) {
                return state;
            }
            return updateMessage(
                state,
                message.with({
                    status: ConversationMessageStatus.UpdatingFeedback,
                })
            );
        case "UPDATE_MESSAGE_FEEDBACK":
            return {
                ...state,
                currentConversation:
                    state.currentConversation?.updateMessageFeedback(
                        action.feedback
                    ),
            };

        case "UPDATE_MEMORY_STATUS": {
            const nextConversation =
                state.currentConversation?.updateMemoryStatus(
                    action.messageId,
                    action.memoryId,
                    action.status
                );

            const updatedMessage = nextConversation?.messages.find(
                (msg) => msg.id === action.messageId
            );

            return {
                ...state,
                currentConversation: nextConversation,
                conversationMessageStore: updatedMessage
                    ? state.conversationMessageStore?.add(updatedMessage)
                    : state.conversationMessageStore,
            };
        }

        case "UPDATE_TOOL_CALL_STATUS":
            const nextConversation =
                state.currentConversation?.updateToolCallStatus(
                    action.messageId,
                    action.toolCallId,
                    action.status
                );

            const updatedMessage = nextConversation?.messages.find(
                (msg) => msg.id === action.messageId
            );

            return {
                ...state,
                currentConversation: nextConversation,
                conversationMessageStore: updatedMessage
                    ? state.conversationMessageStore?.add(updatedMessage)
                    : state.conversationMessageStore,
            };

        case "RETRY":
            return handleRetryOrEdit(action.ids, state);

        case "EDIT":
            return handleRetryOrEdit(action.ids, state);

        case "ERROR":
            return {
                ...state,
                status: ConversationStateStatus.Error,
                error: action.error,
            };

        case "RESET_ERROR":
            return {
                ...state,
                status: ConversationStateStatus.ConversationReady,
                error: undefined,
            };
    }
};

conversationReducer.defaultState = {
    status: ConversationStateStatus.Uninitialized,
    lastAssistantMessageLengthLimited: false,
};

const handleCancelledPrompt = (
    state: CurrentConversationState
): CurrentConversationState => {
    const latestMessage = state.currentConversation?.messages.at(-1);
    if (!latestMessage || latestMessage.role === MessageRole.Assistant) {
        const nextConversationMessageStore =
            state.conversationMessageStore?.removeById(latestMessage?.id);

        return {
            ...state,
            streamingResponseAbortController: undefined,
            status: ConversationStateStatus.ConversationReady,
            conversationMessageStore: nextConversationMessageStore,
            currentConversation: state.currentConversation?.with({
                messages: nextConversationMessageStore?.build() || [],
            }),
        };
    }

    return {
        ...state,
        status: ConversationStateStatus.ConversationReady,
    };
};

function handleRetryOrEdit(
    ids: Array<string>,
    state: CurrentConversationState
) {
    let nextConversationMessageStore = state.conversationMessageStore;

    ids.forEach((id) => {
        nextConversationMessageStore =
            nextConversationMessageStore?.removeById(id);
    });

    return {
        ...state,
        status: ConversationStateStatus.StreamingMessage,
        conversationMessageStore: nextConversationMessageStore,
        currentConversation: state.currentConversation?.with({
            messages: nextConversationMessageStore?.build() || [],
        }),
    };
}

function isConversationLengthLimited(
    conversation: ConversationRecord | undefined
): boolean {
    if (
        !conversation ||
        !conversation.messages ||
        conversation.messages.length === 0
    ) {
        return false;
    }

    // Find the last assistant message
    const lastAssistantMessage = [...conversation.messages]
        .reverse()
        .find((msg) => msg.role === MessageRole.Assistant);

    return lastAssistantMessage?.chatFinishReason === ChatFinishReason.Length;
}

function getNextMessageIndex(
    messages: ConversationMessageRecord[],
    parentId?: string
): number | undefined {
    if (parentId == null || StringUtils.isEmpty(parentId)) {
        return undefined;
    }

    const messageParent = messages.find((m) => m.id === parentId);

    const currentMessageIndex = messageParent?.index ?? 0;

    return currentMessageIndex + 1;
}

function updateMessage(
    state: CurrentConversationState,
    message: ConversationMessageRecord
): CurrentConversationState {
    const { currentConversation, conversationMessageStore } = state;

    if (!currentConversation || !conversationMessageStore) {
        return {
            ...state,
            status: ConversationStateStatus.Error,
            error: new Error("There must be a current conversation to update."),
        };
    }

    const nextMessageIndex =
        message.index ??
        getNextMessageIndex(currentConversation.messages, message.parentId) ??
        1;

    const updatedMessage = message.with({ index: nextMessageIndex });

    // Check if this is an assistant message with Length finish reason
    const isLengthLimited =
        updatedMessage.role === MessageRole.Assistant &&
        updatedMessage.chatFinishReason === ChatFinishReason.Length;

    return {
        ...state,
        ...addMessageToConversation(state, updatedMessage),
        lastAssistantMessageLengthLimited: isLengthLimited,
    };
}

function addMessageToConversation(
    state: Partial<CurrentConversationState>,
    message: ConversationMessageRecord
): Partial<CurrentConversationState> {
    const { currentConversation, conversationMessageStore } = state;

    if (!currentConversation || !conversationMessageStore) {
        return {};
    }

    const nextConversationMessageStore = conversationMessageStore.add(message);

    const nextMessages = nextConversationMessageStore.build();

    return {
        conversationMessageStore: nextConversationMessageStore,
        currentConversation: currentConversation.with({
            messages: nextMessages,
        }),
    };
}
